import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../baseQuery';
import { IPaginationModel } from '@/types/admin/dataTable';
import { 
  IBulletin, 
  IBulletinFilterModel, 
  IBulletinListResponse, 
  IBulletinPhoto, 
  IBulletinSortModel, 
  ICreateBulletin,
  BulletinApiResponse,
  BulletinListApiResponse,
  BulletinUnreadCountResponse
} from '@/types/admin/bulletins';

// Helper function to build query string for bulletins V2 API
const buildBulletinsQuery = ({
  sortModel,
  filterModel,
  paginationModel,
  selectedPropertyId,
  searchQuery,
}: {
  sortModel: IBulletinSortModel[];
  filterModel: IBulletinFilterModel;
  paginationModel: IPaginationModel;
  selectedPropertyId: number;
  searchQuery?: string;
}): URLSearchParams => {
  const searchParams = new URLSearchParams({
    propertyId: selectedPropertyId.toString(),
    showScheduled: (filterModel.showScheduled ?? false).toString(),
    showDraft: (filterModel.showDraft ?? false).toString(),
    recipientType: filterModel.recipientType || 'all',
  });

  // Add search query if present
  if (searchQuery && searchQuery.trim()) {
    searchParams.append('searchQuery', searchQuery.trim());
  }

  // Add pagination (V2 uses pageNum instead of offset)
  if (paginationModel.page > 0) {
    searchParams.append('pageNum', paginationModel.page.toString());
  }

  // Add sorting if present
  if (sortModel.length > 0) {
    const sort = sortModel[0];
    searchParams.append('sortField', sort.field);
    searchParams.append('sortOrder', sort.sort);
  }

  return searchParams;
};

// Helper function to convert form data to FormData for V2 API
export const convertToFormData = (bulletin: ICreateBulletin, bulletinId?: number): FormData => {
  const formData = new FormData();
  
  // Add bulletin ID for editing (required for PATCH requests)
  // Check both the bulletin object and the separate parameter for ID
  const idToUse = (bulletin as any).id || bulletinId;
  if (idToUse) {
    formData.append('id', idToUse.toString());
  }
  
  // Basic fields (matching admin web exactly)
  formData.append('title', bulletin.title);
  formData.append('message', bulletin.message); // Plain text message
  formData.append('messageHtml', bulletin.message); // For now, use same as message (no rich text in RN)
  formData.append('messageJson', JSON.stringify({ blocks: [{ text: bulletin.message }] })); // Simple JSON structure
  formData.append('propertyId', bulletin.propertyId.toString());
  formData.append('sendEmail', bulletin.sendEmail.toString());
  formData.append('pinHomepage', bulletin.pinHomepage.toString());
  formData.append('recipientType', bulletin.recipientType);
  formData.append('highlight', bulletin.highlight.toString());

  // Handle publish date based on publish type (matching admin web logic)
  if (bulletin.publishType === 'publishNow') {
    formData.append('publishDate', new Date().toISOString());
  } else if (bulletin.publishType === 'scheduled' && bulletin.publishDate) {
    formData.append('publishDate', bulletin.publishDate);
  }
  // Note: For 'draft' type, no publishDate is sent (bulletin remains unpublished)
  
  // Note: publishType is not sent to API, only used for logic above

  // Handle photos - send both existing file IDs to preserve AND new file uploads
  if (bulletin.photos && bulletin.photos.length > 0) {
    // Collect existing file IDs (photos without a file object)
    const existingFileIds: number[] = [];
    
    bulletin.photos.forEach((photo: IBulletinPhoto) => {
      if (photo.file) {
        // New file upload - append to files
        formData.append('files', photo.file as any);
      } else if (photo.id) {
        // Existing file - collect ID to preserve
        existingFileIds.push(photo.id);
      }
    });
    
    // Send existing file IDs if any (only when editing)
    if (idToUse && existingFileIds.length > 0) {
      formData.append('existingFileIds', JSON.stringify(existingFileIds));
    }
  }

  return formData;
};

// RTK Query API slice for bulletins V2
export const bulletinsApi = createApi({
  reducerPath: 'bulletinsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['BulletinsV2'],
  endpoints: (builder) => ({
    // Get bulletins list with filtering, sorting, and pagination (V2)
    getBulletinsV2: builder.query<IBulletinListResponse, {
      sortModel: IBulletinSortModel[];
      filterModel: IBulletinFilterModel;
      paginationModel: IPaginationModel;
      selectedPropertyId: number;
      searchQuery?: string;
    }>({
      query: (params) => {
        const searchParams = buildBulletinsQuery(params);
        return {
          url: `/api/v2/bulletins?${searchParams.toString()}`,
          method: 'GET',
        };
      },
      providesTags: (result, error, arg) => [
        { type: 'BulletinsV2', id: `property-${arg.selectedPropertyId}` },
        { type: 'BulletinsV2', id: 'LIST' },
      ],
      // Don't keep unused data - force fresh fetches
      keepUnusedDataFor: 0,
    }),

    // Create bulletin (V2)
    createBulletinV2: builder.mutation<BulletinApiResponse, FormData>({
      query: (formData) => {
        return {
          url: '/api/v2/bulletins',
          method: 'POST',
          body: formData,
          formData: true,
          timeout: 60000, // 60 seconds for file uploads
        };
      },
      transformResponse: (response: BulletinApiResponse) => {
        return response;
      },
      transformErrorResponse: (response: { status: number; data: any }) => {
        console.error('❌ Bulletin API Error:', response);
        return {
          status: response.status,
          data: response.data,
        };
      },
      invalidatesTags: (result, error, formData) => {
        // Invalidate all BulletinsV2 tags to ensure fresh data
        return [
          { type: 'BulletinsV2', id: 'LIST' },
          'BulletinsV2', // Invalidate all other BulletinsV2 tags
        ];
      },
    }),

    // Edit bulletin (V2)
    editBulletinV2: builder.mutation<BulletinApiResponse, FormData>({
      query: (formData) => ({
        url: '/api/v2/bulletins',
        method: 'PATCH',
        body: formData,
        formData: true,
        timeout: 60000, // 60 seconds for file uploads
      }),
      transformResponse: (response: BulletinApiResponse) => {
        return response;
      },
      transformErrorResponse: (response: { status: number; data: any }) => {
        console.error('❌ Edit Bulletin API Error:', response);
        return {
          status: response.status,
          data: response.data,
        };
      },
      invalidatesTags: (result, error, formData) => {
        if (!error) {
          console.log('🔄 Invalidating BulletinsV2 cache tags');
        }
        // Invalidate all BulletinsV2 tags to ensure fresh data
        return [
          { type: 'BulletinsV2', id: 'LIST' },
          'BulletinsV2', // Invalidate all other BulletinsV2 tags
        ];
      },
    }),

    // Delete bulletin (V1 - still used for deletion)
    deleteBulletin: builder.mutation<BulletinApiResponse, IBulletin>({
      query: (bulletin) => ({
        url: '/api/v1/bulletins',
        method: 'DELETE',
        body: bulletin,
      }),
      invalidatesTags: (result, error, bulletin) => {
        // Invalidate all BulletinsV2 tags to ensure fresh data
        return [
          { type: 'BulletinsV2', id: 'LIST' },
          'BulletinsV2', // Invalidate all other BulletinsV2 tags
        ];
      },
    }),

    // Get unread bulletins count
    getUnreadBulletinsCount: builder.query<BulletinUnreadCountResponse, { propertyId: number }>({
      query: ({ propertyId }) => ({
        url: `/api/v2/bulletins/unread?propertyId=${propertyId}`,
        method: 'GET',
      }),
      providesTags: ['BulletinsV2'],
    }),


  }),
});

// Export hooks for usage in functional components
export const {
  useGetBulletinsV2Query,
  useLazyGetBulletinsV2Query,
  useCreateBulletinV2Mutation,
  useEditBulletinV2Mutation,
  useDeleteBulletinMutation,
  useGetUnreadBulletinsCountQuery,
} = bulletinsApi;

